import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TicketListCard } from '../../components/TicketListCard'
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Breadcrumbs,
  Link,
  useTheme,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Autocomplete,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  DataGrid,
  GridRenderCellParams
} from '@mui/x-data-grid'
import {
  Business,
  ArrowBack,
  Edit,
  Email,
  People,
  ConfirmationNumber,
  LocationCity,
  CheckCircle,
  Cancel,
  AutoAwesome,
  Add,
  MoreVert,
  MailOutline,
  LockReset,
  VerifiedUser,
  Delete,
  Link as LinkIcon,
  AccessTime
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { organisationsService } from '../../../../shared/services/OrganisationsService'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import { userService } from '../../../../shared/services/UserService'
import { apiClient } from '../../../../shared/services/ApiClient'
import { UserSummary } from '../../../../shared/types/entities'
import { InviteUserDialog } from '../../components/user/InviteUserDialog'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

interface DepartmentFormData {
  name: string
  description?: string
  type: string
  contactEmail?: string
  phoneNumber?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
}

const departmentSchema = yup.object({
  name: yup.string().required('Name is required').max(200, 'Name too long'),
  description: yup.string().max(1000, 'Description too long'),
  type: yup.string().required('Type is required'),
  contactEmail: yup.string().email('Invalid email').max(256, 'Email too long'),
  phoneNumber: yup.string().max(20, 'Phone number too long'),
  addressLine1: yup.string().max(200, 'Address line 1 too long'),
  addressLine2: yup.string().max(200, 'Address line 2 too long'),
  city: yup.string().max(100, 'City too long'),
  stateCounty: yup.string().max(100, 'State/County too long'),
  postalCode: yup.string().max(20, 'Postal code too long'),
  country: yup.string().max(100, 'Country too long')
})

interface EditUserFormData {
  firstName: string
  lastName: string
  role: string
}

interface EditOrgFormData {
  companyName: string
  contactEmail: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
  isActive: boolean
  notes?: string
}

interface OrganisationSlaOverride {
  enabled: boolean
  policies?: {
    [key: string]: {
      responseHours: number
      resolutionHours: number
    }
  }
}

const editUserSchema = yup.object({
  firstName: yup.string().required('First name is required').max(50, 'First name too long'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name too long'),
  role: yup.string().required('Role is required')
})

const editOrgSchema = yup.object({
  companyName: yup.string().required('Company name is required').max(200, 'Name too long'),
  contactEmail: yup.string().required('Email is required').email('Invalid email'),
  addressLine1: yup.string().max(200, 'Address line 1 too long'),
  addressLine2: yup.string().max(200, 'Address line 2 too long'),
  city: yup.string().max(100, 'City too long'),
  stateCounty: yup.string().max(100, 'State/County too long'),
  postalCode: yup.string().max(20, 'Postal code too long'),
  country: yup.string().max(100, 'Country too long'),
  isActive: yup.boolean().required(),
  notes: yup.string().max(1000, 'Notes too long')
})

function OrganizationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [organization, setOrganization] = useState<any>(null)
  const [parentOrganization, setParentOrganization] = useState<any>(null)
  const [organizations, setOrganizations] = useState<any[]>([])
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createSuccess, setCreateSuccess] = useState(false)
  const [users, setUsers] = useState<UserSummary[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [userMenuAnchor, setUserMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserSummary | null>(null)
  const [selectedEditRole, setSelectedEditRole] = useState<string>('Customer')
  const [selectedEditChildSites, setSelectedEditChildSites] = useState<string[]>([])
  const [editOrgDialogOpen, setEditOrgDialogOpen] = useState(false)

  const [slaOverride, setSlaOverride] = useState<OrganisationSlaOverride>({
    enabled: false,
    policies: {
      'Urgent': { responseHours: 1, resolutionHours: 4 },
      'High': { responseHours: 2, resolutionHours: 8 },
      'Medium': { responseHours: 4, resolutionHours: 24 },
      'Low': { responseHours: 8, resolutionHours: 48 }
    }
  })
  const [slaSaveStatus, setSlaSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<DepartmentFormData>({
    resolver: yupResolver(departmentSchema),
    defaultValues: {
      type: 'Department'
    }
  })

  const {
    register: registerEditUser,
    handleSubmit: handleSubmitEditUser,
    formState: { errors: editUserErrors },
    reset: resetEditUser,
    setValue: setEditUserValue
  } = useForm<EditUserFormData>({
    resolver: yupResolver(editUserSchema)
  })

  const {
    register: registerEditOrg,
    handleSubmit: handleSubmitEditOrg,
    formState: { errors: editOrgErrors },
    reset: resetEditOrg,
    setValue: setEditOrgValue
  } = useForm<EditOrgFormData>({
    resolver: yupResolver(editOrgSchema)
  })

  useEffect(() => {
    if (id) {
      loadOrganization()
      loadOrganizations()
    }
  }, [id])

  useEffect(() => {
    // Load users when Users tab (index 2) is active
    if (tabValue === 2 && id) {
      loadUsers()
    }
  }, [tabValue, id])

  useEffect(() => {
    // Load SLA override when Settings tab (index 4) is active
    if (tabValue === 4 && id) {
      loadSlaOverride()
    }
  }, [tabValue, id])

  const loadOrganization = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await organisationsService.getOrganisation(id)
      setOrganization(data)

      // If this is a child site, load the parent organization
      if (data.customerId) {
        try {
          const parent = await organisationsService.getOrganisation(data.customerId)
          setParentOrganization(parent)
        } catch (err) {
          console.error('Failed to load parent organization:', err)
        }
      } else {
        setParentOrganization(null)
      }
    } catch (err: any) {
      console.error('Failed to load organization:', err)
      setError(err.message || 'Failed to load organisation details')
    } finally {
      setLoading(false)
    }
  }

  const loadOrganizations = async () => {
    if (!id) return

    try {
      const allOrganizations = await OrganizationService.getOrganizations()
      // Filter to only show child sites for this organization (not the org itself)
      const customerOrgs = allOrganizations.filter((org: any) =>
        org.customerId === id && org.childSiteId != null
      )
      setOrganizations(customerOrgs)
    } catch (err: any) {
      console.error('Failed to load organizations:', err)
    }
  }

  const loadUsers = async () => {
    if (!id) return

    try {
      setUsersLoading(true)

      // Fetch both active users and pending invitations
      const [usersResponse, invitationsResponse] = await Promise.all([
        userService.getUsers(1, 100, undefined, undefined, id),
        userService.getPendingInvitations()
      ])

      // Filter invitations for this organization
      const orgInvitations = invitationsResponse.filter(inv => inv.organisationId === id)

      // Map invitations to match UserSummary structure
      const invitationsWithType = orgInvitations.map(inv => ({
        ...inv,
        // Add placeholder fields for grid columns that don't exist on invitations
        userId: inv.invitationId, // Use invitationId as userId for grid
        isActive: false, // Invitations are not active yet
        emailVerified: false, // Not verified until they accept
        assignedTicketCount: 0,
        openTicketCount: 0,
        resolvedTicketCount: 0,
        closedTicketCount: 0,
        lastLoginAt: undefined,
        childSiteName: undefined,
        isPending: true // Flag to indicate this is a pending invitation
      }))

      // Combine users and invitations
      setUsers([...usersResponse.data, ...invitationsWithType])
    } catch (err: any) {
      console.error('Failed to load users:', err)
    } finally {
      setUsersLoading(false)
    }
  }

  const loadSlaOverride = async () => {
    if (!id) return

    try {
      const response = await organisationsService.getOrganizationSla(id)
      setSlaOverride(response)
    } catch (err: any) {
      console.error('Failed to load SLA override:', err)
      // If error, keep default values
    }
  }

  const handleSaveSlaOverride = async () => {
    if (!id) return

    try {
      setSlaSaveStatus('saving')
      await organisationsService.updateOrganizationSla(id, slaOverride)
      setSlaSaveStatus('success')
      setTimeout(() => setSlaSaveStatus('idle'), 3000)
    } catch (err: any) {
      console.error('Failed to save SLA override:', err)
      setSlaSaveStatus('error')
      setTimeout(() => setSlaSaveStatus('idle'), 3000)
    }
  }

  const handleResetSlaOverride = async () => {
    if (!id) return

    try {
      setSlaSaveStatus('saving')
      const resetOverride = { enabled: false, policies: {} }
      await organisationsService.updateOrganizationSla(id, resetOverride)
      setSlaOverride(resetOverride)
      setSlaSaveStatus('success')
      setTimeout(() => setSlaSaveStatus('idle'), 3000)
    } catch (err: any) {
      console.error('Failed to reset SLA override:', err)
      setSlaSaveStatus('error')
      setTimeout(() => setSlaSaveStatus('idle'), 3000)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const generateOrgSlug = (orgName: string) => {
    return orgName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  }

  const handleCreateDepartment = async (data: DepartmentFormData) => {
    if (!id) return

    try {
      setCreateError(null)

      // Build request with only non-empty values
      const request: any = {
        customerId: id,
        name: data.name,
        type: data.type,
        defaultTicketVisibility: 'Personal',
        allowPrivateTickets: false
      }

      // Only add optional fields if they have values
      if (data.description) request.description = data.description
      if (data.contactEmail) request.contactEmail = data.contactEmail
      if (data.phoneNumber) request.phoneNumber = data.phoneNumber
      if (data.addressLine1) request.addressLine1 = data.addressLine1
      if (data.addressLine2) request.addressLine2 = data.addressLine2
      if (data.city) request.city = data.city
      if (data.stateCounty) request.stateCounty = data.stateCounty
      if (data.postalCode) request.postalCode = data.postalCode
      if (data.country) request.country = data.country

      await OrganizationService.createOrganization(request)

      setCreateSuccess(true)
      reset()

      // Reload organizations to show new department
      await loadOrganizations()

      // Close dialog after short delay
      setTimeout(() => {
        setCreateDialogOpen(false)
        setCreateSuccess(false)
      }, 1500)
    } catch (err: any) {
      console.error('Failed to create department:', err)
      setCreateError(err.message || 'Failed to create site/department')
    }
  }

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false)
    setCreateError(null)
    setCreateSuccess(false)
    reset()
  }

  const handleInviteSuccess = () => {
    // Reload users list after successful invite
    loadUsers()
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserSummary) => {
    setUserMenuAnchor(prev => ({ ...prev, [user.userId]: event.currentTarget }))
  }

  const handleUserMenuClose = (userId: string) => {
    setUserMenuAnchor(prev => ({ ...prev, [userId]: null }))
  }

  const handleResendInvitation = async (user: UserSummary) => {
    try {
      await userService.resendInvitation(user.email)
      alert(`Invitation resent successfully to ${user.email}`)
    } catch (err: any) {
      console.error('Failed to resend invitation:', err)
      alert(`Failed to resend invitation: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleResetPassword = async (user: UserSummary) => {
    try {
      await userService.resetPassword(user.email)
      alert(`Password reset link sent successfully to ${user.email}`)
    } catch (err: any) {
      console.error('Failed to send password reset:', err)
      alert(`Failed to send password reset: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleVerifyEmail = async (user: UserSummary) => {
    try {
      await apiClient.post('/auth/admin/verify-user-email', { email: user.email })
      alert(`Email verified successfully for ${user.email}`)
      await loadUsers()
    } catch (err) {
      console.error('Failed to verify email:', err)
      alert('Failed to verify email. Please try again.')
    }
    handleUserMenuClose(user.userId)
  }

  const handleDeleteUser = async (user: UserSummary) => {
    if (!window.confirm(`Are you sure you want to delete ${user.fullName}? This action cannot be undone.`)) {
      handleUserMenuClose(user.userId)
      return
    }

    try {
      await userService.deleteUser(user.userId)
      alert(`User ${user.fullName} deleted successfully`)
      await loadUsers() // Reload user list to reflect deletion
    } catch (err: any) {
      console.error('Failed to delete user:', err)
      alert(`Failed to delete user: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleOpenEditUser = (user: UserSummary) => {
    setSelectedUserForEdit(user)
    setEditUserValue('firstName', user.firstName)
    setEditUserValue('lastName', user.lastName)
    setEditUserValue('role', user.role)
    setSelectedEditRole(user.role)
    setSelectedEditChildSites(user.oversightChildSiteIds || [])
    setEditUserDialogOpen(true)
    handleUserMenuClose(user.userId)
  }

  const handleCloseEditUser = () => {
    setEditUserDialogOpen(false)
    setSelectedUserForEdit(null)
    resetEditUser()
  }

  const handleEditUserSubmit = async (data: EditUserFormData) => {
    if (!selectedUserForEdit) return

    try {
      await userService.updateUser(selectedUserForEdit.userId, {
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        isActive: selectedUserForEdit.isActive,
        oversightChildSiteIds: data.role === 'Oversight' ? selectedEditChildSites : undefined
      })
      alert(`User ${data.firstName} ${data.lastName} updated successfully`)
      await loadUsers() // Reload user list to reflect changes
      handleCloseEditUser()
    } catch (err: any) {
      console.error('Failed to update user:', err)
      alert(`Failed to update user: ${err.message || 'Please try again'}`)
    }
  }

  const handleOpenEditOrg = () => {
    if (!organization) return

    // Pre-populate form with current organization data
    setEditOrgValue('companyName', organization.companyName)
    setEditOrgValue('contactEmail', organization.contactEmail)
    setEditOrgValue('addressLine1', (organization as any).addressLine1 || '')
    setEditOrgValue('addressLine2', (organization as any).addressLine2 || '')
    setEditOrgValue('city', (organization as any).city || '')
    setEditOrgValue('stateCounty', (organization as any).stateCounty || '')
    setEditOrgValue('postalCode', (organization as any).postalCode || '')
    setEditOrgValue('country', (organization as any).country || '')
    setEditOrgValue('isActive', organization.isActive)
    setEditOrgValue('notes', organization.notes || '')

    setEditOrgDialogOpen(true)
  }

  const handleCloseEditOrg = () => {
    setEditOrgDialogOpen(false)
    resetEditOrg()
  }

  const handleEditOrgSubmit = async (data: EditOrgFormData) => {
    if (!organization) return

    try {
      // Include existing billing fields that aren't shown in the form
      await organisationsService.updateOrganisation(organization.customerId, {
        ...data,
        accountStatus: organization.accountStatus,
        billingPlan: organization.billingPlan,
        maxAgents: organization.maxAgents,
        monthlyAmount: organization.monthlyAmount
      })
      alert(`Organisation ${data.companyName} updated successfully`)
      await loadOrganization() // Reload organization to reflect changes
      handleCloseEditOrg()
    } catch (err: any) {
      console.error('Failed to update organization:', err)
      alert(`Failed to update organisation: ${err.message || 'Please try again'}`)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !organization) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Organisation not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/organizations')}>
          Back to Organisations
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/organizations')}
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Organisations
        </Link>
        {parentOrganization && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/admin/organizations/${parentOrganization.customerId}`)}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {parentOrganization.companyName}
          </Link>
        )}
        <Typography color="text.primary">
          {parentOrganization ? organization.name : organization.companyName}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Business fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {organization.companyName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={organization.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={organization.isActive ? 'success' : 'default'}
                icon={organization.isActive ? <CheckCircle /> : <Cancel />}
              />
            </Box>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<Edit />} onClick={handleOpenEditOrg}>
          Edit Organisation
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <People color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {organization.userCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {organization.agentCount} agents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ConfirmationNumber color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Total Tickets
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {organization.ticketCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {organization.openTicketCount} open
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationCity color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Sites/Departments
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {organizations.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sub-organizations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Sites & Departments" />
          <Tab label="Users" />
          <Tab label="Tickets" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Main Contact Email
                      </Typography>
                      <Typography variant="body2">{organization.contactEmail}</Typography>
                    </Box>
                  </Box>
                  {organization.emailAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoAwesome color="success" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Support Ticket Email
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.813rem' }}>
                          {organization.emailAddress}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon color="primary" />
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Customer Portal Link
                      </Typography>
                      <Link
                        href={`https://helpdesk.taylindigital.com/customer?org=${generateOrgSlug(organization.companyName)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.813rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        https://helpdesk.taylindigital.com/customer?org={generateOrgSlug(organization.companyName)}
                      </Link>
                    </Box>
                  </Box>
                  {((organization as any).addressLine1 || (organization as any).city || (organization as any).country) && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationCity color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Address
                        </Typography>
                        <Typography variant="body2">
                          {(organization as any).addressLine1 && <>{(organization as any).addressLine1}<br /></>}
                          {(organization as any).addressLine2 && <>{(organization as any).addressLine2}<br /></>}
                          {(organization as any).city && <>{(organization as any).city}{(organization as any).stateCounty ? ', ' : ''}</>}
                          {(organization as any).stateCounty && <>{(organization as any).stateCounty} </>}
                          {(organization as any).postalCode && <>{(organization as any).postalCode}<br /></>}
                          {(organization as any).country}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {organization.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {organization.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Sites & Departments Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Sites & Departments
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Site/Department
          </Button>
        </Box>

        {organizations.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocationCity sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Sites or Departments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This organization doesn't have any sub-organizations yet.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={organizations.map((org: any) => ({
                id: org.childSiteId,
                name: org.name,
                type: org.type,
                description: org.description,
                contactEmail: org.contactEmail || '-',
                userCount: org.userCount || 0,
                ticketCount: org.ticketCount || 0
              }))}
              columns={[
                {
                  field: 'name',
                  headerName: 'Name',
                  flex: 1,
                  minWidth: 200,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        cursor: 'pointer',
                        '&:hover .org-name': {
                          color: theme.palette.primary.main,
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <LocationCity fontSize="small" />
                      </Box>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography
                          className="org-name"
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 600 }}
                        >
                          {params.row.name}
                        </Typography>
                        {params.row.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {params.row.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                },
                {
                  field: 'type',
                  headerName: 'Type',
                  width: 140,
                  renderCell: (params: GridRenderCellParams) => (
                    <Chip
                      label={params.row.type}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  )
                },
                {
                  field: 'contactEmail',
                  headerName: 'Email',
                  flex: 1,
                  minWidth: 200,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {params.row.contactEmail !== '-' && (
                        <Email sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                      )}
                      <Typography variant="body2" noWrap>
                        {params.row.contactEmail}
                      </Typography>
                    </Box>
                  )
                },
                {
                  field: 'userCount',
                  headerName: 'Users',
                  width: 100,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <People sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                      <Typography variant="body2">{params.row.userCount}</Typography>
                    </Box>
                  )
                },
                {
                  field: 'ticketCount',
                  headerName: 'Tickets',
                  width: 100,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ConfirmationNumber sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                      <Typography variant="body2">{params.row.ticketCount}</Typography>
                    </Box>
                  )
                }
              ]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                }
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                navigate(`/admin/childsites/${params.row.id}`)
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  cursor: 'pointer'
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `2px solid ${theme.palette.divider}`
                }
              }}
            />
          </Box>
        )}
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User
          </Button>
        </Box>

        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <People sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Users
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This organization doesn't have any users yet.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={users.map((user: UserSummary) => ({
                id: user.userId,
                userId: user.userId,
                fullName: user.fullName || `${user.firstName} ${user.lastName}`,
                email: user.email,
                role: user.role,
                lastLoginAt: user.lastLoginAt,
                isActive: user.isActive,
                emailVerified: user.emailVerified,
                assignedTickets: user.assignedTicketCount || 0,
                openTickets: user.openTicketCount || 0,
                childSiteName: user.childSiteName,
                user: user  // Keep reference to full user object for actions
              }))}
              columns={[
                {
                  field: 'fullName',
                  headerName: 'Name',
                  flex: 1,
                  minWidth: 200,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {params.row.fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2)}
                      </Box>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 600 }}
                        >
                          {params.row.fullName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'block', mt: 0.25 }}
                        >
                          {params.row.email}
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
                {
                  field: 'role',
                  headerName: 'Role',
                  width: 140,
                  renderCell: (params: GridRenderCellParams) => (
                    <Chip
                      label={params.row.role}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  )
                },
                {
                  field: 'lastLoginAt',
                  headerName: 'Last Login',
                  flex: 1,
                  minWidth: 150,
                  renderCell: (params: GridRenderCellParams) => (
                    <Typography variant="body2" noWrap>
                      {params.row.lastLoginAt
                        ? new Date(params.row.lastLoginAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })
                        : 'Never'}
                    </Typography>
                  )
                },
                {
                  field: 'assignedTickets',
                  headerName: 'Assigned',
                  width: 100,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ConfirmationNumber sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                      <Typography variant="body2">{params.row.assignedTickets}</Typography>
                    </Box>
                  )
                },
                {
                  field: 'childSiteName',
                  headerName: 'Site/Library',
                  flex: 1,
                  minWidth: 150,
                  renderCell: (params: GridRenderCellParams) => (
                    <Typography variant="body2" noWrap>
                      {params.row.childSiteName || '—'}
                    </Typography>
                  )
                },
                {
                  field: 'isActive',
                  headerName: 'Status',
                  width: 110,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={params.row.isPending ? 'Pending' : (params.row.isActive ? 'Active' : 'Inactive')}
                        size="small"
                        color={params.row.isPending ? 'warning' : (params.row.isActive ? 'success' : 'default')}
                        icon={params.row.isPending ? <AccessTime /> : (params.row.isActive ? <CheckCircle /> : <Cancel />)}
                        sx={{ fontWeight: 500 }}
                      />
                      {!params.row.isPending && !params.row.emailVerified && (
                        <Chip
                          label="Unverified"
                          size="small"
                          color="warning"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                  )
                },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  width: 80,
                  sortable: false,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUserMenuOpen(e, params.row.user)
                        }}
                        sx={{
                          color: theme.palette.grey[600],
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={userMenuAnchor[params.row.userId]}
                        open={Boolean(userMenuAnchor[params.row.userId])}
                        onClose={() => handleUserMenuClose(params.row.userId)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        {!params.row.isPending && (
                          <MenuItem onClick={() => handleOpenEditUser(params.row.user)}>
                            <ListItemIcon>
                              <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit User</ListItemText>
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => handleResendInvitation(params.row.user)}>
                          <ListItemIcon>
                            <MailOutline fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Resend Invitation</ListItemText>
                        </MenuItem>
                        {!params.row.isPending && (
                          <MenuItem onClick={() => handleResetPassword(params.row.user)}>
                            <ListItemIcon>
                              <LockReset fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Reset Password</ListItemText>
                          </MenuItem>
                        )}
                        {!params.row.isPending && !params.row.emailVerified && (
                          <MenuItem onClick={() => handleVerifyEmail(params.row.user)}>
                            <ListItemIcon>
                              <VerifiedUser fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Verify Email</ListItemText>
                          </MenuItem>
                        )}
                        <Divider />
                        <MenuItem onClick={() => handleDeleteUser(params.row.user)} sx={{ color: 'error.main' }}>
                          <ListItemIcon>
                            <Delete fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText>{params.row.isPending ? 'Cancel Invitation' : 'Delete User'}</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )
                }
              ]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                }
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  cursor: 'pointer'
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `2px solid ${theme.palette.divider}`
                }
              }}
            />
          </Box>
        )}
      </TabPanel>

      {/* Tickets Tab */}
      <TabPanel value={tabValue} index={3}>
        {/* Filter by organizationId to show tickets for this org and its child sites */}
        <TicketListCard variant="admin" organizationId={id} />
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Organisation Settings
          </Typography>
          <Button variant="contained" startIcon={<Edit />} onClick={handleOpenEditOrg}>
            Edit Settings
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Organisation Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Organisation Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tenant ID
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.813rem' }}>
                      {organization.tenantId}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </Typography>
                    <Chip
                      label={organization.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={organization.isActive ? 'success' : 'default'}
                      icon={organization.isActive ? <CheckCircle /> : <Cancel />}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Created Date
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {new Date(organization.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          {organization.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {organization.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* SLA Policy Override */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AccessTime color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      SLA Policy Override
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={slaOverride.enabled}
                        onChange={(e) => setSlaOverride({ ...slaOverride, enabled: e.target.checked })}
                      />
                    }
                    label={slaOverride.enabled ? 'Enabled' : 'Disabled'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Override tenant-level SLA policies for this organization
                </Typography>

                {slaOverride.enabled && (
                  <>
                    <Grid container spacing={2}>
                      {['Urgent', 'High', 'Medium', 'Low'].map((priority) => (
                        <Grid item xs={12} sm={6} key={priority}>
                          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {priority} Priority
                              </Typography>
                              <TextField
                                label="Response Time (hours)"
                                type="number"
                                fullWidth
                                size="small"
                                margin="dense"
                                inputProps={{ min: 0.1, max: 168, step: 0.1 }}
                                value={slaOverride.policies?.[priority]?.responseHours || 0}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value)
                                  setSlaOverride({
                                    ...slaOverride,
                                    policies: {
                                      ...slaOverride.policies,
                                      [priority]: {
                                        ...slaOverride.policies?.[priority],
                                        responseHours: value,
                                        resolutionHours: slaOverride.policies?.[priority]?.resolutionHours || 0
                                      }
                                    }
                                  })
                                }}
                              />
                              <TextField
                                label="Resolution Time (hours)"
                                type="number"
                                fullWidth
                                size="small"
                                margin="dense"
                                inputProps={{ min: 0.1, max: 720, step: 0.1 }}
                                value={slaOverride.policies?.[priority]?.resolutionHours || 0}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value)
                                  setSlaOverride({
                                    ...slaOverride,
                                    policies: {
                                      ...slaOverride.policies,
                                      [priority]: {
                                        responseHours: slaOverride.policies?.[priority]?.responseHours || 0,
                                        resolutionHours: value
                                      }
                                    }
                                  })
                                }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={slaSaveStatus === 'saving' ? <CircularProgress size={20} /> : undefined}
                        onClick={handleSaveSlaOverride}
                        disabled={slaSaveStatus === 'saving'}
                      >
                        {slaSaveStatus === 'saving' ? 'Saving...' : 'Save SLA Overrides'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleResetSlaOverride}
                        disabled={slaSaveStatus === 'saving'}
                      >
                        Reset to Tenant Defaults
                      </Button>
                      {slaSaveStatus === 'success' && (
                        <Alert severity="success" sx={{ flex: 1 }}>
                          SLA settings saved successfully
                        </Alert>
                      )}
                      {slaSaveStatus === 'error' && (
                        <Alert severity="error" sx={{ flex: 1 }}>
                          Failed to save SLA settings
                        </Alert>
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Create Site/Department Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Site/Department</DialogTitle>
        <form onSubmit={handleSubmit(handleCreateDepartment)}>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}

            {createSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Site/Department created successfully!
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Name"
                  placeholder="e.g., Main Library, IT Department"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    {...register('type')}
                    defaultValue="Department"
                    label="Type"
                  >
                    <MenuItem value="Library">Library</MenuItem>
                    <MenuItem value="Department">Department</MenuItem>
                    <MenuItem value="Branch">Branch</MenuItem>
                    <MenuItem value="Division">Division</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  placeholder="Brief description of this site or department..."
                  {...register('description')}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email (Optional)"
                  type="email"
                  placeholder="department@example.com"
                  {...register('contactEmail')}
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  placeholder="+1 (555) 123-4567"
                  {...register('phoneNumber')}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1 (Optional)"
                  placeholder="123 Main Street"
                  {...register('addressLine1')}
                  error={!!errors.addressLine1}
                  helperText={errors.addressLine1?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  placeholder="Suite 100"
                  {...register('addressLine2')}
                  error={!!errors.addressLine2}
                  helperText={errors.addressLine2?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  placeholder="London"
                  {...register('city')}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/County (Optional)"
                  placeholder="Greater London"
                  {...register('stateCounty')}
                  error={!!errors.stateCounty}
                  helperText={errors.stateCounty?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code (Optional)"
                  placeholder="SW1A 1AA"
                  {...register('postalCode')}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country (Optional)"
                  placeholder="United Kingdom"
                  {...register('country')}
                  error={!!errors.country}
                  helperText={errors.country?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={createSuccess}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createSuccess}>
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        availableRoles={['Customer', 'Oversight']}
        childSites={organizations}
        organizationId={id}
      />

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onClose={handleCloseEditUser} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <form onSubmit={handleSubmitEditUser(handleEditUserSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  {...registerEditUser('firstName')}
                  error={!!editUserErrors.firstName}
                  helperText={editUserErrors.firstName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  {...registerEditUser('lastName')}
                  error={!!editUserErrors.lastName}
                  helperText={editUserErrors.lastName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={selectedUserForEdit?.email || ''}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!editUserErrors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...registerEditUser('role')}
                    defaultValue={selectedUserForEdit?.role || 'Customer'}
                    label="Role"
                    onChange={(e) => setSelectedEditRole(e.target.value)}
                  >
                    <MenuItem value="Customer">Customer</MenuItem>
                    <MenuItem value="Oversight">Oversight</MenuItem>
                    <MenuItem value="Agent">Agent</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                  </Select>
                  {editUserErrors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {editUserErrors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Show child site multi-select only for Oversight role */}
              {selectedEditRole === 'Oversight' && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={organizations}
                    getOptionLabel={(option: any) => option.name}
                    value={organizations.filter((cs: any) => selectedEditChildSites.includes(cs.childSiteId))}
                    onChange={(_, newValue) => {
                      setSelectedEditChildSites(newValue.map((cs: any) => cs.childSiteId))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Oversight Child Sites"
                        placeholder="Select child sites this user can oversee"
                        helperText="This user will be able to view tickets from selected child sites"
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <li {...props} key={option.childSiteId}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.type}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditUser}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Organisation Dialog */}
      <Dialog open={editOrgDialogOpen} onClose={handleCloseEditOrg} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organisation</DialogTitle>
        <form onSubmit={handleSubmitEditOrg(handleEditOrgSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  {...registerEditOrg('companyName')}
                  error={!!editOrgErrors.companyName}
                  helperText={editOrgErrors.companyName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  {...registerEditOrg('contactEmail')}
                  error={!!editOrgErrors.contactEmail}
                  helperText={editOrgErrors.contactEmail?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1 (Optional)"
                  placeholder="123 Main Street"
                  {...registerEditOrg('addressLine1')}
                  error={!!editOrgErrors.addressLine1}
                  helperText={editOrgErrors.addressLine1?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  placeholder="Suite 100"
                  {...registerEditOrg('addressLine2')}
                  error={!!editOrgErrors.addressLine2}
                  helperText={editOrgErrors.addressLine2?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  placeholder="London"
                  {...registerEditOrg('city')}
                  error={!!editOrgErrors.city}
                  helperText={editOrgErrors.city?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/County (Optional)"
                  placeholder="Greater London"
                  {...registerEditOrg('stateCounty')}
                  error={!!editOrgErrors.stateCounty}
                  helperText={editOrgErrors.stateCounty?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code (Optional)"
                  placeholder="SW1A 1AA"
                  {...registerEditOrg('postalCode')}
                  error={!!editOrgErrors.postalCode}
                  helperText={editOrgErrors.postalCode?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country (Optional)"
                  placeholder="United Kingdom"
                  {...registerEditOrg('country')}
                  error={!!editOrgErrors.country}
                  helperText={editOrgErrors.country?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      {...registerEditOrg('isActive')}
                      defaultChecked={organization?.isActive}
                    />
                  }
                  label="Organisation Active"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes (Optional)"
                  placeholder="Add any notes about this customer organisation..."
                  {...registerEditOrg('notes')}
                  error={!!editOrgErrors.notes}
                  helperText={editOrgErrors.notes?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditOrg}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default OrganizationDetail
